import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  ArrowRight,
  Brain,
  Sparkles,
  Split,
  Eye,
  EyeOff,
  UtensilsCrossed,
  Leaf,
  ChefHat,
  Ban,
  XCircle,
  Upload,
  Folder,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MenuItemSearchCombobox, type SearchableItem } from './MenuItemSearchCombobox';
import { useMenuCategories } from '@/hooks/useMenuCategories';
import { useMenuItems } from '@/hooks/useMenuItems';
import { useIngredients } from '@/hooks/useIngredients';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { resetCurrentUserPOSTrainingData } from '@/lib/posDataReset';
import {
  type POSSalesItem,
  type POSSalesItemGroup,
  type MappingType,
  groupSimilarPOSItems,
  ungroupItems,
  findBestMatch,
  savePOSSalesItemMapping,
  getPOSSalesItemMappings,
  loadPOSItemsFromReports,
} from '@/lib/posSalesItemMapping';
import { PLATFORMS, normalizePlatform, platformDisplayName } from '@/lib/platformConstants';

interface POSReportsStep3TrainingProps {
  onNext: () => void;
  onPrevious: () => void;
  platform?: string | null; // Optional - if not provided, loads all platforms
}

export const POSReportsStep3Training = ({ onNext, onPrevious, platform }: POSReportsStep3TrainingProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [itemGroups, setItemGroups] = useState<POSSalesItemGroup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSimilar, setShowSimilar] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [activePlatforms, setActivePlatforms] = useState<string[]>([]);
  const [hasReportsUploaded, setHasReportsUploaded] = useState(false);
  const [totalPosItemsFound, setTotalPosItemsFound] = useState(0);
  const [resetting, setResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  
  // Ungroup functionality
  const [ungroupMode, setUngroupMode] = useState(false);
  const [selectedItemsForUngroup, setSelectedItemsForUngroup] = useState<Set<string>>(new Set());
  
  // Form state
  const [mappingType, setMappingType] = useState<MappingType>('menu_item');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<SearchableItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null); // For menu_category mapping type
  
  // Load menu categories, menu items, and ingredients
  const { data: menuCategories = [] } = useMenuCategories();
  const { data: menuItems = [] } = useMenuItems();
  const { data: ingredients = [] } = useIngredients();
  
  // Stats (calculated early so they can be used in useEffect)
  const totalGroups = itemGroups.length;
  const trainedGroups = itemGroups.filter(g => g.isTrained).length;
  const totalItems = itemGroups.reduce((sum, g) => sum + g.items.length, 0);
  const trainedItems = itemGroups
    .filter(g => g.isTrained)
    .reduce((sum, g) => sum + g.items.length, 0);
  const remainingGroups = totalGroups - trainedGroups;
  
  // Load data on mount
  useEffect(() => {
    loadData();
  }, [platform]);
  
  // Auto-navigate to review when all items are mapped
  useEffect(() => {
    if (dataLoaded && remainingGroups === 0 && trainedGroups > 0) {
      const timer = setTimeout(() => {
        onNext();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [dataLoaded, remainingGroups, trainedGroups, onNext]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setLoading(false);
        setDataLoaded(true);
        return;
      }
      
      // If a specific platform is provided, use it; otherwise, load all active platforms
      let platformsToLoad: string[] = [];
      
      if (platform) {
        platformsToLoad = [platform];
      } else {
        // Get all active POS integrations
        const { data: integrations, error: intError } = await supabase
          .from('company_integrations')
          .select('integration_type')
          .eq('company_id', companyId)
          .in('integration_type', [PLATFORMS.TOAST, PLATFORMS.DOORDASH, 'ubereats'])
          .eq('is_active', true);
        
        if (intError) throw intError;
        
        platformsToLoad = (integrations || []).map(i => i.integration_type);
      }
      
      if (platformsToLoad.length === 0) {
        setItemGroups([]);
        setLoading(false);
        setDataLoaded(true);
        return;
      }
      
      setActivePlatforms(platformsToLoad);
      
      // Check if any reports have been uploaded
      const { data: reportsCheck } = await supabase
        .from('pos_report_uploads')
        .select('id')
        .eq('company_id', companyId)
        .eq('report_type', 'item_quantities')
        .eq('status', 'completed')
        .in('platform', platformsToLoad)
        .limit(1);
      
      setHasReportsUploaded(!!reportsCheck && reportsCheck.length > 0);
      
      // Load POS items from all platforms
      const allPosItems: POSSalesItem[] = [];
      const platformItemCounts: Record<string, number> = {};
      
      // Use console.warn for better visibility (less likely to be filtered)
      console.warn('🔍 Loading POS items for platforms:', platformsToLoad);
      
      for (const plat of platformsToLoad) {
        console.warn(`📦 Loading items for platform: ${plat}`);
        const posItems = await loadPOSItemsFromReports(companyId, plat);
        platformItemCounts[plat] = posItems.length;
        console.warn(`✅ Found ${posItems.length} items for platform ${plat}`);
        if (posItems.length > 0) {
          console.warn(`   Sample items:`, posItems.slice(0, 3).map(i => `${i.name} (${i.platform})`));
        }
        allPosItems.push(...posItems);
      }
      
      const totalItems = allPosItems.length;
      const itemsByPlatform = allPosItems.reduce((acc, item) => {
        acc[item.platform] = (acc[item.platform] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      console.warn(`📊 Total POS items loaded: ${totalItems}`);
      console.warn(`📊 Items by platform:`, itemsByPlatform);
      console.warn(`📊 Platform item counts:`, platformItemCounts);
      
      // Show toast with summary for debugging
      if (totalItems > 0) {
        const platformSummary = Object.entries(itemsByPlatform)
          .map(([plat, count]) => `${platformDisplayName(plat)}: ${count}`)
          .join(', ');
        toast({
          title: 'Data Loaded',
          description: `Loaded ${totalItems} items from: ${platformSummary}`,
          duration: 5000,
        });
      } else {
        toast({
          title: 'No Items Found',
          description: 'No POS items found for any platform. Please upload reports first.',
          variant: 'destructive',
          duration: 5000,
        });
      }
      
      // Track total items found before filtering
      setTotalPosItemsFound(allPosItems.length);
      
      if (allPosItems.length === 0) {
        setItemGroups([]);
        setLoading(false);
        setDataLoaded(true);
        return;
      }
      
      // Load existing mappings for all platforms
      const allMappedIdentifiers = new Set<string>();
      
      for (const plat of platformsToLoad) {
        const existingMappings = await getPOSSalesItemMappings(companyId, plat);
        const platNorm = normalizePlatform(plat);
        existingMappings.forEach(m => {
          allMappedIdentifiers.add(`${platNorm}:${m.pos_item_identifier}`);
          if (m.platform) {
            allMappedIdentifiers.add(`${normalizePlatform(m.platform)}:${m.pos_item_identifier}`);
          }
        });
      }
      
      // Filter out already mapped items
      // Check both the item's platform and try matching with all platforms in case of cross-platform grouping
      const unmappedItems = allPosItems.filter(item => {
        const normalizedPlatform = normalizePlatform(item.platform);
        
        // Extract the original identifier from the unique ID (remove platform prefix if present)
        // The item.id might be "platform:identifier" or just "identifier"
        const itemIdentifier = item.id.includes(':') 
          ? item.id.split(':').slice(1).join(':')
          : item.id;
        
        const key = `${normalizedPlatform}:${itemIdentifier}`;
        
        // Check if this specific item is mapped
        const isMapped = allMappedIdentifiers.has(key);
        
        if (isMapped) {
          console.warn(`Filtering out mapped item: ${item.name} (${normalizedPlatform}:${itemIdentifier})`);
        }
        
        return !isMapped;
      });
      
      const unmappedByPlatform = unmappedItems.reduce((acc, item) => {
        acc[item.platform] = (acc[item.platform] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      console.warn(`🔍 After filtering: ${unmappedItems.length} unmapped items remaining`);
      console.warn(`🔍 Unmapped items by platform:`, unmappedByPlatform);
      
      // Show warning if some platforms have no unmapped items
      const platformsWithNoItems = platformsToLoad.filter(plat => !unmappedByPlatform[plat] || unmappedByPlatform[plat] === 0);
      if (platformsWithNoItems.length > 0 && unmappedItems.length > 0) {
        console.warn(`⚠️ Platforms with no unmapped items:`, platformsWithNoItems);
        toast({
          title: 'Some platforms have no items',
          description: `${platformsWithNoItems.map(p => platformDisplayName(p)).join(', ')} have no unmapped items. They may be already mapped or have no reports.`,
          variant: 'default',
          duration: 5000,
        });
      }
      
      if (unmappedItems.length === 0) {
        setItemGroups([]);
        setLoading(false);
        setDataLoaded(true);
        return;
      }
      
      // Group similar items
      console.warn('🔄 Starting grouping of unmapped items...');
      const groups = groupSimilarPOSItems(unmappedItems);
      console.warn(`✅ Created ${groups.length} groups after grouping`);
      
      // Log groups by platform
      const groupsSummary = groups.map(g => ({
        patternName: g.patternName,
        platform: g.platform,
        itemCount: g.items.length,
        platforms: Array.from(new Set(g.items.map(i => i.platform)))
      }));
      console.warn('📋 Groups summary:', groupsSummary);
      
      // Count cross-platform groups
      const crossPlatformGroups = groups.filter(g => g.platform.includes(','));
      console.warn(`🌐 Cross-platform groups: ${crossPlatformGroups.length} out of ${groups.length}`);
      
      if (crossPlatformGroups.length > 0) {
        console.warn('🌐 Cross-platform groups:', crossPlatformGroups.map(g => 
          `${g.patternName} (${g.platform})`
        ));
      }
      
      // Get AI suggestions for each group
      const groupsWithSuggestions = await Promise.all(
        groups.map(async (group) => {
          try {
            const suggestion = await findBestMatch(group.items[0], companyId);
            return { ...group, suggestion };
          } catch {
            return group;
          }
        })
      );
      
      console.log(`Final groups with suggestions: ${groupsWithSuggestions.length}`);
      setItemGroups(groupsWithSuggestions);
      
      // Initialize form - check if suggestion is an ingredient first
      if (groupsWithSuggestions.length > 0 && groupsWithSuggestions[0].suggestion?.mappedItem) {
        const suggestion = groupsWithSuggestions[0].suggestion;
        if (suggestion.mappedItem.type === 'ingredient') {
          setMappingType('ingredient');
          setSelectedItemId(suggestion.mappedItem.id);
          setSelectedItem({
            id: suggestion.mappedItem.id,
            name: suggestion.mappedItem.name,
            type: 'ingredient',
            category: suggestion.mappedItem.category,
          });
          setSelectedCategoryId(null);
        } else {
          setMappingType('menu_item');
          setSelectedItemId(null);
          setSelectedItem(null);
          setSelectedCategoryId(null);
        }
      } else {
        setMappingType('menu_item');
        setSelectedItemId(null);
        setSelectedItem(null);
        setSelectedCategoryId(null);
      }
      
      setLoading(false);
      setDataLoaded(true);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load items',
        variant: 'destructive',
      });
      setLoading(false);
      setDataLoaded(true);
    }
  };
  
  const handleSave = async () => {
    if (!mappingType) {
      toast({
        title: 'Mapping type required',
        description: 'Please select how to map this item',
        variant: 'destructive',
      });
      return;
    }
    
    // Validation based on mapping type
    if (mappingType === 'ingredient' && !selectedItemId) {
      toast({
        title: 'Ingredient required',
        description: 'Please select an ingredient to map to',
        variant: 'destructive',
      });
      return;
    }
    
    if (mappingType === 'menu_item' && !selectedItemId) {
      toast({
        title: 'Menu item required',
        description: 'Please select a menu item to map to',
        variant: 'destructive',
      });
      return;
    }
    
    if (mappingType === 'menu_category' && (!selectedCategoryId || !selectedItemId)) {
      toast({
        title: 'Selection required',
        description: 'Please select a category and menu item to map to',
        variant: 'destructive',
      });
      return;
    }
    
    setSaving(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      const currentGroup = itemGroups[currentIndex];
      const groupPlatform = currentGroup?.platform || platform;
      if (!companyId || !groupPlatform) throw new Error('Company ID or platform not found');
      
      // Save mapping for all items in the group
      for (const item of currentGroup.items) {
        // Each item should have its own platform, but fallback to first platform in group if needed
        let itemPlatform = item.platform;
        if (!itemPlatform && groupPlatform.includes(',')) {
          // If group has multiple platforms, use the first one (shouldn't happen if items are properly set)
          itemPlatform = groupPlatform.split(',')[0].trim();
        } else if (!itemPlatform) {
          itemPlatform = groupPlatform;
        }
        // Normalize platform name to match database values
        const normalizedPlatform = itemPlatform.toLowerCase().trim();
        
        // Extract the original identifier from the unique ID (remove platform prefix)
        // The ID format is "platform:identifier", so we need to extract just the identifier part
        const posItemIdentifier = item.id.includes(':') 
          ? item.id.split(':').slice(1).join(':')  // Handle cases where identifier itself might contain ':'
          : item.id;
        
        console.log(`Saving mapping for item: ${item.name} (platform: ${normalizedPlatform}, identifier: ${posItemIdentifier})`);
        
        // For menu_category type, use the category ID instead of the menu item ID
        const mappedItemId = mappingType === 'menu_category' ? selectedCategoryId : selectedItemId;
        
        await savePOSSalesItemMapping(
          companyId,
          normalizedPlatform,
          posItemIdentifier,
          item.name,
          mappingType,
          mappedItemId,
          false,
          currentGroup.suggestion?.confidence
        );
      }
      
      // Mark group as trained
      const updatedGroups = itemGroups.map((g, idx) => 
        idx === currentIndex ? { ...g, isTrained: true } : g
      );
      setItemGroups(updatedGroups);
      
      // Update training progress for each platform
      // Handle cross-platform groups by counting items per individual platform
      const platformCounts = new Map<string, { trained: number; total: number }>();
      
      updatedGroups.forEach(g => {
        // Extract individual platforms from group (handle comma-separated)
        const platforms = g.platform.includes(',') 
          ? g.platform.split(',').map(p => p.trim())
          : [g.platform];
        
        platforms.forEach(plat => {
          if (!platformCounts.has(plat)) {
            platformCounts.set(plat, { trained: 0, total: 0 });
          }
          const counts = platformCounts.get(plat)!;
          
          // Count items for this specific platform in the group
          const itemsForPlatform = g.items.filter(item => item.platform === plat);
          counts.total += itemsForPlatform.length;
          if (g.isTrained) {
            counts.trained += itemsForPlatform.length;
          }
        });
      });
      
      // Update progress for each platform
      for (const [plat, counts] of platformCounts.entries()) {
        await supabase
          .from('pos_training_progress')
          .upsert({
            company_id: companyId,
            platform: plat,
            items_mapped: counts.trained,
            total_pos_items: counts.total,
            status: 'mapping',
          }, {
            onConflict: 'company_id,platform',
          });
      }
      
      toast({
        title: 'Saved!',
        description: `Mapped ${currentGroup.items.length} item${currentGroup.items.length > 1 ? 's' : ''}`,
      });
      
      // Move to next untrained group
      goToNextUntrainedWithState(updatedGroups);
      
    } catch (error: any) {
      console.error('Error saving:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleSkip = () => {
    goToNextUntrained();
  };
  
  const goToNextUntrained = () => {
    goToNextUntrainedWithState(itemGroups);
  };
  
  const goToNextUntrainedWithState = (groups: POSSalesItemGroup[]) => {
    const nextIndex = groups.findIndex((g, idx) => idx > currentIndex && !g.isTrained);
    
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
      // Always reset to menu_item mapping (default)
      setMappingType('menu_item');
      setSelectedItemId(null);
      setSelectedItem(null);
      setSelectedCategoryId(null);
      
      setShowSimilar(false);
      setUngroupMode(false);
      setSelectedItemsForUngroup(new Set());
    }
  };
  
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      // Always reset to menu_item mapping (default)
      setMappingType('menu_item');
      setSelectedItemId(null);
      setSelectedItem(null);
      setSelectedCategoryId(null);
      
      setShowSimilar(false);
      setUngroupMode(false);
      setSelectedItemsForUngroup(new Set());
    }
  };
  
  const goToNext = () => {
    if (currentIndex < itemGroups.length - 1) {
      setCurrentIndex(currentIndex + 1);
      // Always reset to menu_item mapping (default)
      setMappingType('menu_item');
      setSelectedItemId(null);
      setSelectedItem(null);
      setSelectedCategoryId(null);
      
      setShowSimilar(false);
      setUngroupMode(false);
      setSelectedItemsForUngroup(new Set());
    }
  };
  
  const handleUseSuggestion = () => {
    const currentGroup = itemGroups[currentIndex];
    if (currentGroup?.suggestion?.mappedItem) {
      const suggestion = currentGroup.suggestion.mappedItem;
      setMappingType(suggestion.type);
      setSelectedItemId(suggestion.id);
      setSelectedItem({
        id: suggestion.id,
        name: suggestion.name,
        type: suggestion.type,
        category: suggestion.category,
        unit: suggestion.unit,
      });
      // Clear category for ingredient/menu item suggestions
      if (suggestion.type === 'ingredient' || suggestion.type === 'menu_item') {
        setSelectedCategoryId(null);
      }
    }
  };
  
  const handleAutoMatch = async () => {
    setAutoMatching(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');
      
      let matchedCount = 0;
      const updatedGroups = [...itemGroups];
      
      for (let i = 0; i < updatedGroups.length; i++) {
        const group = updatedGroups[i];
        if (group.isTrained) continue;
        
        // Use suggestion if confidence is high enough
        // Prioritize ingredient matches
        if (group.suggestion?.mappedItem && group.suggestion.confidence >= 0.7) {
          const mappedType = group.suggestion.mappedItem.type;
          // Only auto-match ingredients and menu items (not prep items for now)
          if (mappedType === 'ingredient' || mappedType === 'menu_item') {
            for (const item of group.items) {
              // Each item should have its own platform, but fallback to first platform in group if needed
              let itemPlatform = item.platform;
              if (!itemPlatform && group.platform.includes(',')) {
                // If group has multiple platforms, use the first one (shouldn't happen if items are properly set)
                itemPlatform = group.platform.split(',')[0].trim();
              } else if (!itemPlatform) {
                itemPlatform = group.platform;
              }
              // Normalize platform name to match database values
              const normalizedPlatform = itemPlatform.toLowerCase().trim();
              
              // Extract the original identifier from the unique ID (remove platform prefix)
              const posItemIdentifier = item.id.includes(':') 
                ? item.id.split(':').slice(1).join(':')
                : item.id;
              
              await savePOSSalesItemMapping(
                companyId,
                normalizedPlatform,
                posItemIdentifier,
                item.name,
                mappedType,
                group.suggestion.mappedItem.id,
                true,
                group.suggestion.confidence
              );
            }
            updatedGroups[i] = { ...group, isTrained: true };
            matchedCount++;
          }
        }
      }
      
      setItemGroups(updatedGroups);
      
      // Update progress for each platform
      // Handle cross-platform groups by counting items per individual platform
      const platformCounts = new Map<string, { trained: number; total: number }>();
      
      updatedGroups.forEach(g => {
        // Extract individual platforms from group (handle comma-separated)
        const platforms = g.platform.includes(',') 
          ? g.platform.split(',').map(p => p.trim())
          : [g.platform];
        
        platforms.forEach(plat => {
          if (!platformCounts.has(plat)) {
            platformCounts.set(plat, { trained: 0, total: 0 });
          }
          const counts = platformCounts.get(plat)!;
          
          // Count items for this specific platform in the group
          const itemsForPlatform = g.items.filter(item => item.platform === plat);
          counts.total += itemsForPlatform.length;
          if (g.isTrained) {
            counts.trained += itemsForPlatform.length;
          }
        });
      });
      
      // Update progress for each platform
      for (const [plat, counts] of platformCounts.entries()) {
        await supabase
          .from('pos_training_progress')
          .upsert({
            company_id: companyId,
            platform: plat,
            items_mapped: counts.trained,
            total_pos_items: counts.total,
            status: 'mapping',
          }, {
            onConflict: 'company_id,platform',
          });
      }
      
      toast({
        title: 'Auto-match complete',
        description: `Matched ${matchedCount} groups with high confidence`,
      });
      
      // Move to first untrained group
      const nextIndex = updatedGroups.findIndex(g => !g.isTrained);
      if (nextIndex !== -1) {
        setCurrentIndex(nextIndex);
        // Always reset to menu_item mapping (default)
        setMappingType('menu_item');
        setSelectedItemId(null);
        setSelectedItem(null);
        setSelectedCategoryId(null);
      }
    } catch (error: any) {
      console.error('Error auto-matching:', error);
      toast({
        title: 'Error',
        description: 'Failed to auto-match items',
        variant: 'destructive',
      });
    } finally {
      setAutoMatching(false);
    }
  };
  
  // Toggle item selection for ungroup
  const toggleItemSelection = (itemId: string) => {
    setSelectedItemsForUngroup(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };
  
  // Handle ungroup action
  const handleUngroup = () => {
    if (selectedItemsForUngroup.size === 0) {
      toast({
        title: 'No items selected',
        description: 'Please select items to ungroup',
        variant: 'destructive',
      });
      return;
    }
    
    const currentGroup = itemGroups[currentIndex];
    const itemsToKeep = currentGroup.items.filter(item => !selectedItemsForUngroup.has(item.id));
    
    if (itemsToKeep.length === 0) {
      toast({
        title: 'Cannot ungroup all',
        description: 'At least one item must remain in the group',
        variant: 'destructive',
      });
      return;
    }
    
    const selectedIds = Array.from(selectedItemsForUngroup);
    const updatedGroups = ungroupItems(itemGroups, currentGroup.id, selectedIds);
    
    // Find the index of the current group after ungrouping (it should still exist with remaining items)
    const newCurrentIndex = updatedGroups.findIndex(g => g.id === currentGroup.id);
    
    setItemGroups(updatedGroups);
    setSelectedItemsForUngroup(new Set());
    setUngroupMode(false);
    
    // Update current index to stay on the same group (with remaining items)
    // If the group was removed (shouldn't happen based on our check), go to the first group
    if (newCurrentIndex !== -1) {
      setCurrentIndex(newCurrentIndex);
    } else if (updatedGroups.length > 0) {
      setCurrentIndex(0);
    }
    
    toast({
      title: 'Items ungrouped',
      description: `${selectedIds.length} item(s) moved to new groups`,
    });
  };
  
  const handleMappingTypeChange = (type: string) => {
    if (type.startsWith('category:')) {
      const categoryId = type.replace('category:', '');
      setMappingType('menu_category');
      setSelectedCategoryId(categoryId);
      setSelectedItemId(null);
      setSelectedItem(null);
    } else {
      setMappingType(type as MappingType);
      setSelectedCategoryId(null);
      setSelectedItemId(null);
      setSelectedItem(null);
    }
  };
  
  const handleCategoryChange = (categoryName: string | null) => {
    setSelectedCategory(categoryName);
    // Clear selected item when category changes to avoid mismatched selections
    setSelectedItemId(null);
    setSelectedItem(null);
  };
  
  const handleItemSelect = (itemId: string | null, item: SearchableItem | null) => {
    setSelectedItemId(itemId);
    setSelectedItem(item);
    if (item) {
      setMappingType(item.type);
    }
  };

  const handleResetAll = async () => {
    try {
      setResetting(true);
      await resetCurrentUserPOSTrainingData(undefined, false);
      
      // Reload data to reflect the reset
      await loadData();
      
      toast({
        title: 'Success',
        description: 'All POS reports and mappings have been reset. You can now start fresh.',
      });
      
      setShowResetDialog(false);
    } catch (error: any) {
      console.error('Error resetting POS data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset POS data',
        variant: 'destructive',
      });
    } finally {
      setResetting(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-2 w-full" />
          </CardContent>
        </Card>
        <Card className="border-2">
          <CardHeader className="pb-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="space-y-6">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // No items to map - differentiate between "no reports uploaded" and "all items mapped"
  if (dataLoaded && itemGroups.length === 0) {
    // Check if we have reports but no items (all mapped) vs no reports at all
    const allItemsAlreadyMapped = hasReportsUploaded && totalPosItemsFound > 0;
    const noReportsOrNoItems = !hasReportsUploaded || totalPosItemsFound === 0;
    
    if (noReportsOrNoItems) {
      return (
        <div className="space-y-6">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-700">
              <div className="text-center py-6 space-y-4">
                <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                  <Upload className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-lg text-amber-800">No POS Items Found</h4>
                  <p className="text-amber-700">
                    {!hasReportsUploaded 
                      ? "Please upload your Item Quantities report in the previous step first. This report contains the POS items that need to be mapped to your menu."
                      : "The uploaded report doesn't contain any item identifiers to map. Please ensure you're uploading the correct Item Quantities report."}
                  </p>
                </div>
              </div>
            </AlertDescription>
          </Alert>
          
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={onPrevious}>← Previous Step</Button>
            <Button variant="outline" onClick={onNext}>
              Skip Mapping
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      );
    }
    
    // All items have been mapped (from initial load - no groups to train)
    // This will be handled by the auto-navigate useEffect above
    return (
      <div className="space-y-6">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-lg">All Items Mapped!</h4>
                <p className="text-muted-foreground">
                  All POS items have been mapped. Redirecting to review screen...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All trained - show completion message
  if (dataLoaded && remainingGroups === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-lg">Mapping Complete!</h4>
                <p className="text-muted-foreground">
                  You've mapped {trainedItems} items across {trainedGroups} groups.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Redirecting to review screen...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const currentGroup = itemGroups[currentIndex];
  const similarCount = currentGroup?.items.length || 0;
  const currentGroupPlatform = currentGroup?.platform || platform || 'POS';
  
  // Handle comma-separated platforms (cross-platform groups)
  const platformsInGroup = currentGroupPlatform.includes(',')
    ? currentGroupPlatform.split(',').map(p => p.trim())
    : [currentGroupPlatform];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-2">Map POS Items to Menu</h3>
          <p className="text-sm text-muted-foreground">
            Map POS items to your menu items, ingredients, or prep items. Similar items are grouped together.
          </p>
        </div>
        {(itemGroups.length > 0 || trainedGroups > 0) && (
          <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={resetting}
              >
                {resetting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset All Data
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset All POS Training Data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>All uploaded POS reports</li>
                    <li>All POS item mappings</li>
                    <li>All training progress</li>
                  </ul>
                  <p className="mt-2 font-medium text-destructive">
                    This action cannot be undone. You will need to re-upload all reports and re-map all items.
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleResetAll}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Reset All Data
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      
      {/* Progress Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium flex items-center gap-2">
              <Brain className="h-4 w-4 text-primary" />
              Training Progress
            </span>
            <span className="text-sm text-muted-foreground">
              {trainedGroups} of {totalGroups} groups ({trainedItems} items)
            </span>
          </div>
          <Progress value={(trainedGroups / totalGroups) * 100} className="h-2" />
        </CardContent>
      </Card>
      
      {/* Debug Info Card - Shows which platforms have data */}
      {activePlatforms.length > 0 && (
        <Card className="bg-muted/30 border-muted">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-4 text-xs">
              <span className="font-medium">Active Platforms:</span>
              {activePlatforms.map(plat => {
                const normalizedPlat = normalizePlatform(plat);
                const platformItems = itemGroups
                  .flatMap(g => g.items)
                  .filter(item => normalizePlatform(item.platform) === normalizedPlat).length;
                const platformGroups = itemGroups.filter(g => {
                  const groupPlatforms = g.platform.includes(',') 
                    ? g.platform.split(',').map(p => normalizePlatform(p.trim()))
                    : [normalizePlatform(g.platform)];
                  return groupPlatforms.includes(normalizedPlat);
                }).length;
                return (
                  <div key={plat} className="flex items-center gap-1">
                    <Badge variant="outline" className="text-xs">
                      {platformDisplayName(plat)}
                    </Badge>
                    <span className="text-muted-foreground">
                      ({platformItems} items, {platformGroups} groups)
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Auto-match Button */}
      {remainingGroups > 1 && (
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            onClick={handleAutoMatch}
            disabled={autoMatching}
          >
            {autoMatching ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Matching...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Auto-Match High Confidence
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={goToPrevious}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Prev
        </Button>
        <span className="text-sm text-muted-foreground">
          Group {currentIndex + 1} of {totalGroups}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={goToNext}
          disabled={currentIndex === itemGroups.length - 1}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      {/* Main Card */}
      {currentGroup && (
        <Card className={cn(
          "border-2 transition-colors",
          currentGroup.isTrained ? "border-emerald-200 bg-emerald-50/30" : "border-primary/20"
        )}>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-xl flex items-center gap-2 flex-wrap">
                  "{currentGroup.patternName}"
                  {platformsInGroup.map(plat => (
                    <Badge key={plat} variant="secondary" className="text-xs">
                      {platformDisplayName(plat)}
                    </Badge>
                  ))}
                  {currentGroup.isTrained && (
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Mapped
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="mt-1 flex items-center gap-2">
                  {similarCount > 1 ? (
                    <>
                      <span>+{similarCount - 1} similar items</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => setShowSimilar(!showSimilar)}
                      >
                        {showSimilar ? (
                          <>
                            <EyeOff className="h-3 w-3 mr-1" />
                            Hide
                          </>
                        ) : (
                          <>
                            <Eye className="h-3 w-3 mr-1" />
                            Show
                          </>
                        )}
                      </Button>
                      {!ungroupMode && similarCount > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() => setUngroupMode(true)}
                        >
                          <Split className="h-3 w-3 mr-1" />
                          Ungroup
                        </Button>
                      )}
                    </>
                  ) : (
                    <span>Single item</span>
                  )}
                </CardDescription>
              </div>
              
              {/* AI Suggestion Badge */}
              {currentGroup.suggestion?.mappedItem && !currentGroup.isTrained && (
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200"
                  onClick={handleUseSuggestion}
                >
                  <Sparkles className="h-3 w-3 mr-1 text-purple-600" />
                  <span className="text-purple-700">
                    {currentGroup.suggestion.mappedItem.name}
                  </span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {Math.round(currentGroup.suggestion.confidence * 100)}%
                  </Badge>
                </Button>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Similar Items List (when expanded) */}
            {(showSimilar || ungroupMode) && similarCount > 1 && (
              <div className="border rounded-lg p-3 bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {ungroupMode ? 'Select items to ungroup:' : 'Items in this group:'}
                  </span>
                  {ungroupMode && (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUngroupMode(false);
                          setSelectedItemsForUngroup(new Set());
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleUngroup}
                        disabled={selectedItemsForUngroup.size === 0}
                      >
                        Ungroup ({selectedItemsForUngroup.size})
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {currentGroup.items.map((item, idx) => (
                    <div
                      key={item.id}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded text-sm",
                        ungroupMode && "hover:bg-muted cursor-pointer",
                        !ungroupMode && "cursor-default",
                        selectedItemsForUngroup.has(item.id) && "bg-primary/10"
                      )}
                      onClick={ungroupMode ? () => toggleItemSelection(item.id) : undefined}
                    >
                      {ungroupMode && (
                        <Checkbox
                          id={`ungroup-checkbox-${item.id}`}
                          checked={selectedItemsForUngroup.has(item.id)}
                          onCheckedChange={(checked) => {
                            // Handle both boolean and "indeterminate" states
                            if (checked === true) {
                              if (!selectedItemsForUngroup.has(item.id)) {
                                toggleItemSelection(item.id);
                              }
                            } else if (checked === false) {
                              if (selectedItemsForUngroup.has(item.id)) {
                                toggleItemSelection(item.id);
                              }
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <span className="flex-1 truncate">{item.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {platformDisplayName(item.platform)}
                      </Badge>
                      {item.totalQuantity !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          {item.totalQuantity} sold
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Mapping Controls */}
            {!ungroupMode && !currentGroup.isTrained && (
              <>
                {/* Mapping Type Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Map to:</label>
                  <Select 
                    value={mappingType || ''} 
                    onValueChange={(type) => {
                      handleMappingTypeChange(type as MappingType);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select mapping type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="menu_item">
                        <div className="flex items-center gap-2">
                          <UtensilsCrossed className="h-4 w-4 text-blue-600" />
                          Menu Item
                        </div>
                      </SelectItem>
                      <SelectItem value="ingredient">
                        <div className="flex items-center gap-2">
                          <Leaf className="h-4 w-4 text-green-600" />
                          Ingredient
                        </div>
                      </SelectItem>
                      {menuCategories.length > 0 && (
                        <>
                          {menuCategories.map(category => (
                            <SelectItem key={category.id} value={`category:${category.id}`}>
                              <div className="flex items-center gap-2">
                                <Folder className="h-4 w-4 text-indigo-600" />
                                {category.name}
                              </div>
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Menu Item Selection (when mapping type is menu_item, no category) */}
                {mappingType === 'menu_item' && !selectedCategoryId && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Select Menu Item:</label>
                    <Select 
                      value={selectedItemId || ''} 
                      onValueChange={(itemId) => {
                        const selectedMenuItem = menuItems.find(item => item.id === itemId);
                        if (selectedMenuItem) {
                          setSelectedItemId(itemId);
                          setSelectedItem({
                            id: selectedMenuItem.id,
                            name: selectedMenuItem.name,
                            type: 'menu_item',
                            category: selectedMenuItem.category,
                          });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a menu item" />
                      </SelectTrigger>
                      <SelectContent>
                        {menuItems.map(item => (
                          <SelectItem key={item.id} value={item.id}>
                            <div className="flex items-center gap-2">
                              <UtensilsCrossed className="h-4 w-4 text-blue-600" />
                              {item.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Ingredient Selection (when mapping type is ingredient) */}
                {mappingType === 'ingredient' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Select Ingredient:</label>
                    <Select 
                      value={selectedItemId || ''} 
                      onValueChange={(itemId) => {
                        const selectedIngredient = ingredients.find(ing => ing.id === itemId);
                        if (selectedIngredient) {
                          setSelectedItemId(itemId);
                          setSelectedItem({
                            id: selectedIngredient.id,
                            name: selectedIngredient.name,
                            type: 'ingredient',
                            category: selectedIngredient.category,
                          });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select an ingredient" />
                      </SelectTrigger>
                      <SelectContent>
                        {ingredients.map(ingredient => (
                          <SelectItem key={ingredient.id} value={ingredient.id}>
                            <div className="flex items-center gap-2">
                              <Leaf className="h-4 w-4 text-green-600" />
                              {ingredient.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Category Selection (when a category is selected from dropdown) */}
                {mappingType === 'menu_category' && selectedCategoryId && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Select Menu Item from Category:</label>
                      <Select 
                        value={selectedItemId || ''} 
                        onValueChange={(itemId) => {
                          const selectedMenuItem = menuItems.find(item => item.id === itemId);
                          if (selectedMenuItem) {
                            setSelectedItemId(itemId);
                            setSelectedItem({
                              id: selectedMenuItem.id,
                              name: selectedMenuItem.name,
                              type: 'menu_item',
                              category: selectedMenuItem.category,
                            });
                            // Keep mapping type as 'menu_category' when selecting from category
                            // This preserves the category mapping context
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a menu item from this category" />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            const categoryMenuItems = menuItems.filter(item => 
                              item.categoryId === selectedCategoryId
                            );
                            
                            if (categoryMenuItems.length === 0) {
                              return (
                                <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                  No menu items found in this category
                                </div>
                              );
                            }
                            
                            return categoryMenuItems.map(item => (
                              <SelectItem key={item.id} value={item.id}>
                                <div className="flex items-center gap-2">
                                  <UtensilsCrossed className="h-4 w-4 text-blue-600" />
                                  {item.name}
                                </div>
                              </SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                
                {/* Mapping info */}
                {mappingType === 'ingredient' && selectedItemId && (
                  <Alert>
                    <Leaf className="h-4 w-4" />
                    <AlertDescription>
                      This POS item will be mapped to ingredient "{ingredients.find(ing => ing.id === selectedItemId)?.name}".
                    </AlertDescription>
                  </Alert>
                )}
                
                {mappingType === 'menu_item' && selectedItemId && !selectedCategoryId && (
                  <Alert>
                    <UtensilsCrossed className="h-4 w-4" />
                    <AlertDescription>
                      This POS item will be mapped to menu item "{menuItems.find(item => item.id === selectedItemId)?.name}".
                    </AlertDescription>
                  </Alert>
                )}
                
                {selectedCategoryId && selectedItemId && mappingType === 'menu_category' && (
                  <Alert>
                    <Folder className="h-4 w-4" />
                    <AlertDescription>
                      This POS item will be mapped to the "{menuCategories.find(c => c.id === selectedCategoryId)?.name}" category.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
            
            
            {/* Already mapped display */}
            {currentGroup.isTrained && (
              <Alert className="bg-emerald-50 border-emerald-200">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-700">
                  This group has been mapped. Use navigation to review or edit other groups.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button variant="outline" onClick={onPrevious}>
          ← Previous Step
        </Button>
        
        <div className="flex gap-2">
          {!currentGroup?.isTrained && (
            <>
              <Button variant="ghost" onClick={handleSkip}>
                Skip
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Save & Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </>
          )}
          {currentGroup?.isTrained && remainingGroups === 0 && (
            <Button onClick={onNext}>
              Continue to Review
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default POSReportsStep3Training;
